export type TServerSortDirection = 'asc' | 'desc';

export type TServerSort = {
  field: string;
  direction: TServerSortDirection;
} | null;

export interface IIdentificationNumberSortable {
  identificationNumber?: string | null;
}

export const buildSortQuery = (sort: TServerSort): string | undefined =>
  sort ? `${sort.field},${sort.direction}` : undefined;

export const sortByIdentificationNumber = <T extends IIdentificationNumberSortable>(
  applications: T[],
  sort: TServerSort
): T[] => {
  if (!sort || sort.field !== 'identificationNumber') {
    return applications;
  }

  const directionMultiplier = sort.direction === 'desc' ? -1 : 1;

  return [...applications].sort((left, right) => {
    const leftValue = (left.identificationNumber ?? '').trim();
    const rightValue = (right.identificationNumber ?? '').trim();

    if (!leftValue && !rightValue) return 0;
    if (!leftValue) return 1 * directionMultiplier;
    if (!rightValue) return -1 * directionMultiplier;

    return (
      leftValue.localeCompare(rightValue, 'uk', {
        numeric: true,
        sensitivity: 'base',
      }) * directionMultiplier
    );
  });
};

const getComparableValue = (value: unknown, field: string): number | string => {
  if (value === null || value === undefined) {
    return '';
  }

  if (field === 'createdAt' || field === 'updatedAt') {
    const date = new Date(String(value));
    return Number.isNaN(date.getTime()) ? '' : date.getTime();
  }

  return String(value).trim();
};

export const sortByActiveSort = <T extends Record<string, unknown>>(items: T[], sort: TServerSort): T[] => {
  if (!sort) {
    return items;
  }

  if (sort.field === 'identificationNumber') {
    return sortByIdentificationNumber(items as T[], sort);
  }

  const directionMultiplier = sort.direction === 'desc' ? -1 : 1;
  const sortField = sort.field;

  return [...items].sort((left, right) => {
    const leftRaw = left[sortField];
    const rightRaw = right[sortField];

    const leftEmpty = leftRaw === null || leftRaw === undefined || leftRaw === '';
    const rightEmpty = rightRaw === null || rightRaw === undefined || rightRaw === '';

    if (leftEmpty && rightEmpty) return 0;
    if (leftEmpty) return 1 * directionMultiplier;
    if (rightEmpty) return -1 * directionMultiplier;

    const leftValue = getComparableValue(leftRaw, sortField);
    const rightValue = getComparableValue(rightRaw, sortField);

    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
      return (leftValue - rightValue) * directionMultiplier;
    }

    return (
      String(leftValue).localeCompare(String(rightValue), undefined, {
        numeric: true,
        sensitivity: 'base',
      }) * directionMultiplier
    );
  });
};
